// The explorer's file filter: type chips (language groups and their
// extensions) and a path regular expression. Both live in the URL query
// (`types=`, `path=`) and combine with the explorer's other filters; see
// docs/design.md, "Diff explorer".
import type { LanguageInfo } from '../engine/types';

// ---------------------------------------------------------------- type classification

/** Lockfiles by exact file name: their own group, whatever their extension. */
export const LOCKFILES = ['Cargo.lock', 'pnpm-lock.yaml', 'package-lock.json', 'yarn.lock', 'bun.lock'];

/** Groups for common types the engine may not list, and the catch-alls. */
const BUILTIN: LanguageInfo[] = [
  { id: 'svelte', name: 'Svelte', extensions: ['svelte'] },
  { id: 'dart', name: 'Dart', extensions: ['dart'] },
  { id: 'markdown', name: 'Markdown', extensions: ['md', 'markdown', 'mdx'] },
  { id: 'json', name: 'JSON', extensions: ['json', 'jsonc', 'json5'] },
  { id: 'yaml', name: 'YAML', extensions: ['yaml', 'yml'] },
  { id: 'toml', name: 'TOML', extensions: ['toml'] },
  { id: 'images', name: 'Images', extensions: ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'avif', 'ico', 'bmp', 'tif', 'tiff'] },
];

/** Engine languages shown as part of another group (TSX is TypeScript to a reviewer). */
const MERGE: Record<string, string> = { tsx: 'typescript' };

/** Multi-part extensions kept apart from their last part. */
const COMPOUND = ['d.ts', 'd.mts', 'd.cts'];

export const LOCKFILES_GROUP = 'lockfiles';
export const OTHER_GROUP = 'other';

export interface FileType {
  /** Group ID: an engine language ID, a built-in group, `lockfiles` or `other`. */
  group: string;
  /**
   * The file's type within its group: `.ext` (lowercased; `.d.ts` for
   * declaration files; `.gitignore` for a dotfile), a lockfile's name, or
   * the file name of a file with no extension (`Makefile`).
   */
  key: string;
}

function basename(p: string): string {
  return p.slice(p.lastIndexOf('/') + 1);
}

/**
 * Maps paths to type groups and keys. Groups come from the engine's
 * language list plus the built-in ones; unknown extensions are `other`.
 */
export class TypeClassifier {
  private extGroup = new Map<string, string>();
  private names = new Map<string, string>();

  constructor(languages: LanguageInfo[] = []) {
    for (const l of languages) {
      const id = MERGE[l.id] ?? l.id;
      if (!this.names.has(id)) this.names.set(id, MERGE[l.id] ? (languages.find((x) => x.id === id)?.name ?? l.name) : l.name);
      for (const e of l.extensions) if (!this.extGroup.has(e.toLowerCase())) this.extGroup.set(e.toLowerCase(), id);
    }
    for (const l of BUILTIN) {
      if (!this.names.has(l.id)) this.names.set(l.id, l.name);
      for (const e of l.extensions) if (!this.extGroup.has(e)) this.extGroup.set(e, l.id);
    }
    this.names.set(LOCKFILES_GROUP, 'Lockfiles');
    this.names.set(OTHER_GROUP, 'Other');
  }

  /** The group and key of a repository path. */
  classify(path: string): FileType {
    const name = basename(path);
    if (LOCKFILES.includes(name)) return { group: LOCKFILES_GROUP, key: name };
    const dot = name.lastIndexOf('.');
    if (dot < 0) return { group: OTHER_GROUP, key: name };
    if (dot === 0) {
      // A dotfile (".gitignore"): its name is its type.
      const key = name.toLowerCase();
      return { group: this.keyGroup(key), key };
    }
    const lower = name.toLowerCase();
    const compound = COMPOUND.find((c) => lower.endsWith('.' + c) && lower.length > c.length + 1);
    const key = '.' + (compound ?? lower.slice(dot + 1));
    return { group: this.keyGroup(key), key };
  }

  /** The group a key belongs to. */
  keyGroup(key: string): string {
    if (LOCKFILES.includes(key)) return LOCKFILES_GROUP;
    if (!key.startsWith('.')) return OTHER_GROUP;
    let ext = key.slice(1).toLowerCase();
    const compound = COMPOUND.find((c) => ext === c);
    if (compound) ext = compound.slice(compound.lastIndexOf('.') + 1);
    return this.extGroup.get(ext) ?? OTHER_GROUP;
  }

  isGroup(token: string): boolean {
    return this.names.has(token);
  }

  groupName(id: string): string {
    return this.names.get(id) ?? id;
  }

  /** The group a selection token stands for (itself for a group). */
  tokenGroup(token: string): string {
    return this.isGroup(token) ? token : this.keyGroup(token);
  }
}

// ---------------------------------------------------------------- type selection

/**
 * Whether `path` passes a type selection. Nothing selected passes every
 * file. A selected group passes all its files, unless keys of that group
 * are selected too: then only those keys pass (keys narrow their group).
 */
export function typeMatcher(selected: Iterable<string>, c: TypeClassifier): (path: string) => boolean {
  const groups = new Set<string>();
  const keys = new Map<string, Set<string>>();
  for (const t of selected) {
    if (c.isGroup(t)) groups.add(t);
    else {
      const g = c.keyGroup(t);
      if (!keys.has(g)) keys.set(g, new Set());
      keys.get(g)!.add(t);
    }
  }
  if (!groups.size && !keys.size) return () => true;
  return (path) => {
    const t = c.classify(path);
    const k = keys.get(t.group);
    return k ? k.has(t.key) : groups.has(t.group);
  };
}

/** Chip state of a group: whole group, some of its keys, or not selected. */
export function groupState(selected: string[], group: string, c: TypeClassifier): 'all' | 'some' | 'none' {
  if (selected.some((t) => !c.isGroup(t) && c.keyGroup(t) === group)) return 'some';
  return selected.includes(group) ? 'all' : 'none';
}

/**
 * Toggle a chip. A group chip turns its whole group on, or off (with any
 * of its keys). A key chip turns that key on or off; turning one on drops
 * the group token, since keys narrow their group anyway.
 */
export function toggleType(selected: string[], token: string, c: TypeClassifier): string[] {
  if (c.isGroup(token)) {
    const state = groupState(selected, token, c);
    const rest = selected.filter((t) => t !== token && (c.isGroup(t) || c.keyGroup(t) !== token));
    return state === 'none' ? [...rest, token] : rest;
  }
  if (selected.includes(token)) return selected.filter((t) => t !== token);
  const g = c.keyGroup(token);
  return [...selected.filter((t) => t !== g), token];
}

/** "Only this": the chip alone. */
export function onlyType(token: string): string[] {
  return [token];
}

export interface TypeCount {
  group: string;
  name: string;
  count: number;
  keys: { key: string; count: number }[];
}

/**
 * Chips for a scope: every group and key present in `present`, counted
 * over `counted` (the files the other filters leave), most files first.
 */
export function tallyTypes(present: Iterable<string>, counted: Iterable<string>, c: TypeClassifier): TypeCount[] {
  const groups = new Map<string, Map<string, number>>();
  for (const p of present) {
    const t = c.classify(p);
    if (!groups.has(t.group)) groups.set(t.group, new Map());
    const keys = groups.get(t.group)!;
    if (!keys.has(t.key)) keys.set(t.key, 0);
  }
  for (const p of counted) {
    const t = c.classify(p);
    const keys = groups.get(t.group);
    if (keys?.has(t.key)) keys.set(t.key, keys.get(t.key)! + 1);
  }
  const out: TypeCount[] = [];
  for (const [group, keys] of groups) {
    const list = [...keys].map(([key, count]) => ({ key, count })).sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
    out.push({ group, name: c.groupName(group), count: list.reduce((n, k) => n + k.count, 0), keys: list });
  }
  // Other last; otherwise by count, then name.
  return out.sort(
    (a, b) => Number(a.group === OTHER_GROUP) - Number(b.group === OTHER_GROUP) || b.count - a.count || a.name.localeCompare(b.name),
  );
}

/** Types a breakdown names before "and N more". */
export const BREAKDOWN_MAX = 8;

/**
 * What a group chip stands for, type by type, most files first (".dart 31
 * · .yaml 5 · Makefile 2"): said over a group of more than one type, and
 * over Other always, whose name says nothing of what is in it. Null when
 * there is nothing to add to the chip's own name.
 */
export function typeBreakdown(t: TypeCount, max = BREAKDOWN_MAX): string | null {
  if (t.keys.length < 2 && t.group !== OTHER_GROUP) return null;
  const keys = t.keys.filter((k) => k.count > 0);
  if (!keys.length) return null;
  const named = keys.slice(0, max).map((k) => `${k.key} ${k.count}`);
  const rest = keys.length - named.length;
  return named.join(' · ') + (rest > 0 ? ` · and ${rest} more` : '');
}

/** Lines reviewed of the lines to review. */
export interface LinesDone { done: number; total: number }

/**
 * Change blocks reviewed of the change blocks counted (each as the
 * reader's "Change 2 / 20" counts them), and how many files are not
 * counted yet (their blocks unknown until their diff is computed): with
 * any, the figures are lower bounds.
 */
export interface BlocksDone { done: number; total: number; unknown: number }

/**
 * Change blocks per chip: each group's and key's blocks over `paths` (as
 * `tallyProgress` sums lines), from `reviewed` (each change block of a
 * file, whether it is reviewed; null while unknown, counted as unknown).
 */
export function tallyBlocks(paths: Iterable<string>, reviewed: (path: string) => readonly boolean[] | null, c: TypeClassifier): Map<string, BlocksDone> {
  const out = new Map<string, BlocksDone>();
  for (const p of paths) {
    const flags = reviewed(p);
    const t = c.classify(p);
    for (const token of new Set([t.group, t.key])) {
      const sum = out.get(token) ?? { done: 0, total: 0, unknown: 0 };
      if (flags) out.set(token, { ...sum, done: sum.done + flags.filter(Boolean).length, total: sum.total + flags.length });
      else out.set(token, { ...sum, unknown: sum.unknown + 1 });
    }
  }
  return out;
}

/**
 * The chips in the order their weight says, heaviest first: by changed
 * lines where `lines` knows them (a type with none, after), then by file
 * count, then name; Other last. Where the change is, not how many files.
 */
export function byWeight(types: readonly TypeCount[], lines?: ReadonlyMap<string, LinesDone>): TypeCount[] {
  const weight = (t: TypeCount) => lines?.get(t.group)?.total ?? 0;
  return [...types].sort(
    (a, b) => Number(a.group === OTHER_GROUP) - Number(b.group === OTHER_GROUP) || weight(b) - weight(a) || b.count - a.count || a.name.localeCompare(b.name),
  );
}

/**
 * Review progress per chip: the lines reviewed of each group and key,
 * summed over `paths` (the changed files the other filters keep, as the
 * chips count them) by `progress`. A type with nothing to review is left out.
 */
export function tallyProgress(paths: Iterable<string>, progress: (path: string) => LinesDone, c: TypeClassifier): Map<string, LinesDone> {
  const out = new Map<string, LinesDone>();
  for (const p of paths) {
    const { done, total } = progress(p);
    if (!total) continue;
    const t = c.classify(p);
    for (const token of new Set([t.group, t.key])) {
      const sum = out.get(token) ?? { done: 0, total: 0 };
      out.set(token, { done: sum.done + done, total: sum.total + total });
    }
  }
  return out;
}

// ---------------------------------------------------------------- path regex

export const PATH_MAX = 500;

export interface PathFilter {
  /** The raw text, as typed (and as in the URL). */
  source: string;
  /** null when empty or invalid. */
  re: RegExp | null;
  /** A leading `!`: paths that match are excluded. */
  negate: boolean;
  /** Why the pattern is ignored, or null. */
  error: string | null;
}

/** `/pattern/i`: the only flag form, so a pattern like `/src/` stays a plain pattern. */
const IGNORE_CASE = /^\/([\s\S]*)\/i$/;

/**
 * Parse a path filter: a JavaScript regular expression over the full
 * repository path, case-sensitive. `/pattern/i` matches ignoring case
 * (other slashes are part of the pattern: `/src/` matches "/src/"); a
 * leading `!` excludes matching paths. Invalid or over-long patterns
 * carry an error and filter nothing.
 */
export function parsePathFilter(raw: string): PathFilter {
  const source = raw;
  const text = raw.trim();
  const none = { source, re: null, negate: false, error: null };
  if (!text) return none;
  if (text.length > PATH_MAX) return { ...none, error: `longer than ${PATH_MAX} characters` };
  const negate = text.startsWith('!');
  let body = negate ? text.slice(1) : text;
  let flags = '';
  const m = IGNORE_CASE.exec(body);
  if (m) {
    body = m[1];
    flags = 'i';
  }
  if (!body) return { ...none, negate, error: negate ? 'nothing after !' : 'empty pattern' };
  try {
    return { source, re: new RegExp(body, flags), negate, error: null };
  } catch (e) {
    const msg = e instanceof Error ? e.message.replace(/^Invalid regular expression: /, '') : String(e);
    return { ...none, negate, error: msg };
  }
}

/** Whether a path passes a path filter (always, when it is empty or invalid). */
export function pathPasses(f: PathFilter, path: string): boolean {
  if (!f.re) return true;
  return f.re.test(path) !== f.negate;
}

// ---------------------------------------------------------------- the combined filter and the URL

export const FILTER_PARAMS = ['types', 'path'] as const;

export interface FileFilter {
  types: string[];
  path: PathFilter;
  /** A type selection or a valid path pattern applies. */
  active: boolean;
  test: (path: string) => boolean;
}

/** Selection tokens from `types=` (comma-separated, deduplicated). */
export function parseTypes(value: string | null): string[] {
  if (!value) return [];
  return [...new Set(value.split(',').map((s) => s.trim()).filter(Boolean))];
}

/** `types=` for a selection (null removes it). */
export function formatTypes(selected: string[]): string | null {
  return selected.length ? selected.join(',') : null;
}

/** The filter the URL query describes. */
export function readFilter(query: URLSearchParams, c: TypeClassifier): FileFilter {
  const types = parseTypes(query.get('types'));
  const path = parsePathFilter(query.get('path') ?? '');
  const byType = typeMatcher(types, c);
  const active = types.length > 0 || !!path.re;
  return { types, path, active, test: active ? (p) => byType(p) && pathPasses(path, p) : () => true };
}

/** Query parameters for a filter (null removes one). */
export function filterQuery(types: string[], path: string): Record<(typeof FILTER_PARAMS)[number], string | null> {
  return { types: formatTypes(types), path: path.trim() ? path : null };
}

/**
 * `href` with the filter parameters of `from` (the current query) added,
 * for links between the views of the same pull request.
 */
export function carryFilter(href: string, from: URLSearchParams): string {
  const url = new URL(href, 'http://x');
  let changed = false;
  for (const k of FILTER_PARAMS) {
    const v = from.get(k);
    if (v !== null && !url.searchParams.has(k)) {
      url.searchParams.set(k, v);
      changed = true;
    }
  }
  return changed ? url.pathname + url.search + url.hash : href;
}

/**
 * The types a collapsed "+N more" chip holds, said together: their lines
 * reviewed of their lines changed and their blocks (summed from each
 * type's `progress` and `blocks`, as the chips have them), whether any is
 * still counting, and each type's own line ("Python 2.4k lines, 10%
 * reviewed") for its tooltip. Types with nothing to review count no lines.
 */
export function hiddenTally(
  hidden: readonly TypeCount[],
  progress?: ReadonlyMap<string, LinesDone>,
  blocks?: ReadonlyMap<string, BlocksDone>,
): { lines: LinesDone; blocks: BlocksDone; counting: boolean; each: string[] } {
  const lines = { done: 0, total: 0 };
  const b = { done: 0, total: 0, unknown: 0 };
  const each: string[] = [];
  for (const t of hidden) {
    const p = progress?.get(t.group);
    const k = blocks?.get(t.group);
    if (p) {
      lines.done += p.done;
      lines.total += p.total;
    }
    if (k) {
      b.done += k.done;
      b.total += k.total;
      b.unknown += k.unknown;
    }
    const pct = p && p.total ? Math.floor((p.done / p.total) * 100) : null;
    each.push(`${t.name} ${p ? `${p.total.toLocaleString('en-US')} lines${pct === null ? '' : `, ${pct}% reviewed`}` : `${t.count} file${t.count === 1 ? '' : 's'}`}${k?.unknown ? ' (still counting)' : ''}`);
  }
  return { lines, blocks: b, counting: b.unknown > 0, each };
}
